import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../../util/reactIntl';
import { ExternalLink, NamedLink } from '../../../../../components';

import css from './PriorityLinks.module.css';
import { GUEST, HOST } from '../../../../../util/types';
import { useSelector } from 'react-redux';

/**
 * Create component that shows only a single "Post a new listing" link.
 *
 * @param {*} props contains customLinksMenuClass
 * @returns div with only one link inside.
 */
export const CreateListingMenuLink = props => {
  return (
    <div className={props.customLinksMenuClass}>
      <NamedLink name="NewListingPage" className={classNames(css.priorityLink, css.highlight)}>
        <span className={css.priorityLinkLabel}>
          <FormattedMessage id="TopbarDesktop.createListing" />
        </span>
      </NamedLink>
    </div>
  );
};

/**
 * Link component that can be used on TopbarDesktop.
 *
 * @param {*} props containing linkConfig including resolved 'route' params for NamedLink.
 * @returns NamedLink or ExternalLink component based on config.
 */
const PriorityLink = ({
  linkConfig,
  // setAddPayout = () => {},
  setPendingProfile = () => {},
  // setIdVerificationModal = () => {},
  currentUser,
  // isStripeAccountConnected,
}) => {
  const { text, type, href, route, highlight } = linkConfig;
  const classes = classNames(
    css.priorityLink,
    text === 'Create New Experience' && css.experienceButton,
    { [css.highlight]: highlight }
  );
  if (
    text == 'Create New Experience' &&
    !currentUser?.attributes?.profile?.publicData?.ProfileListingApproved
  ) {
    return (
      <div className={classes}>
        <button type="button" onClick={() => setPendingProfile()} className={css.stripeButton}>
          {text}
        </button>
      </div>
    );
  }
  // if (text == 'Create New Experience' && !isStripeAccountConnected) {
  //   return (
  //     <div className={classes}>
  //       <button type="button" onClick={() => setAddPayout()} className={css.stripeButton}>
  //         {text}
  //       </button>
  //     </div>
  //   );
  // }
  if (type === 'internal' && route) {
    const { name, params, to } = route || {};
    return (
      <NamedLink name={name} params={params} to={to} className={classes}>
        <span className={css.priorityLinkLabel}>{text}</span>
      </NamedLink>
    );
  }
  return (
    <ExternalLink href={href} className={classes}>
      <span className={css.priorityLinkLabel}>{text}</span>
    </ExternalLink>
  );
};

/**
 * Create priority links, which are visible on the desktop layout on the Topbar.
 *
 * @param {*} props contains links array and setLinks function
 * @returns container div with priority links included.
 */
const PriorityLinks = props => {
  const stripeAccountDetails = useSelector(state => state?.stripeConnectAccount?.stripeAccount);

  const { currentUser } = props || {};
  const { stripeAccount } = currentUser || {};
  const isStripeAccountConnected =
    stripeAccountDetails &&
    stripeAccountDetails?.attributes?.stripeAccountData?.requirements?.currently_due?.length == 0;

  const containerRef = useRef(null);
  // Filter out "Post a new listing" early
  const rawLinks = props.links || [];

  const filteredLinks =
    props?.userType == HOST
      ? rawLinks.filter(link => link.text != 'Home')
      : props?.userType == GUEST
      ? []
      : rawLinks?.filter(link => link?.text !== 'Create New Experience');
  const { priorityLinks } = props;

  const isServer = typeof window === 'undefined';
  const isMeasured =
    rawLinks?.[0]?.width && (priorityLinks.length === 0 || priorityLinks?.[0]?.width);
  const styleWrapper = !!isMeasured
    ? {}
    : {
        style: {
          position: 'absolute',
          top: '-2000px',
          left: '-2000px',
          width: '100%',
          height: 'var(--topbarHeightDesktop)',
          display: 'flex',
          flexDirection: 'row',
        },
      };

  const linkConfigs = isMeasured ? priorityLinks : filteredLinks;

  useEffect(() => {
    const isAlreadyMeasured = rawLinks?.[0]?.width;
    if (containerRef.current && !isAlreadyMeasured) {
      const linksFromRenderedWrapper = [...containerRef.current.childNodes];
      let cumulatedWidth = 0;

      // Make sure we're measuring the filtered links
      const linksWithWidths = filteredLinks?.reduce((acc, link, i) => {
        const width = linksFromRenderedWrapper[i]?.offsetWidth || 0; // safe access
        cumulatedWidth += width;
        return [...acc, { ...link, width, cumulatedWidth }];
      }, []);

      props.setLinks(linksWithWidths);
    }
  }, [props?.userType]);

  const content = (
    <div className={css.priorityLinkWrapper} {...styleWrapper} ref={containerRef}>
      {linkConfigs?.map((linkConfig, index) => (
        <PriorityLink
          setPendingProfile={props?.setPendingProfile}
          setIdVerificationModal={props?.setIdVerificationModal}
          setAddPayout={props?.setAddPayout}
          key={`${linkConfig.text}_${index}`}
          linkConfig={linkConfig}
          isStripeAccountConnected={isStripeAccountConnected}
          currentUser={currentUser}
        />
      ))}
    </div>
  );

  return isMeasured || isServer ? content : ReactDOM.createPortal(content, document.body);
};

export default PriorityLinks;
